/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import Stack, { FormStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import AssociatedCareTeamMember from './AssociatedCareTeamMember';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import { TabView } from '../../../../v2BaseComponents/TabView';
import { getIntl } from '../../../../../utils/intl';
import { CenteredFormRow } from '../../../../v2BaseComponents/Form';
import { ABAC, APITypes } from '../../../../../primary-record-types';
import { Field } from 'formik';
import { getSpacing } from '../../../../../utils/spacing';
import CollaboratorHeader from './CollaboratorHeader';
import ContactDetailsDisplay from '../../../../shared/ContactDetailsDisplay';
import { useCollaboratorInvoices } from '../../../../../hooks/api/useCollaboratorInvoices';
import { isPresent } from 'ts-is-present';
import CollaboratorInvoicesTab from './CollaboratorInvoicesTab';
import useEvaluatedPermissions from '../../../../../hooks/api/useEvaluatedPermissions';

const intl = getIntl('profile-collaborator-details-organization-collaborator-view', {
    organizationDetails: 'Collaborator Details',
    permissions: 'Permissions',
    invoices: 'Invoices',
    updateCollaborator: 'Update Collaborator',
    deleteCollaborator: 'Delete Collaborator',
    disclaimer:
        'Any user working on behalf of this professional organization can access this profile with the following permissions.',
});

type OrganizationCollaboratorViewProps = {
    isDisabled: boolean;
    medicalProfileId: string;
    collaboratorId: string;
    collaborator?: APITypes.medicalProfileService.MedicalProfileOrganizationCollaborator;
    initialAppPermissions: ABAC.AppPermissions;
};

type Tabs = 'details' | 'permissions' | 'invoices';

const OrganizationCollaboratorView: React.FC<OrganizationCollaboratorViewProps> = ({
    medicalProfileId,
    collaboratorId,
    isDisabled,
    collaborator,
    initialAppPermissions,
}) => {
    const { permissions } = useEvaluatedPermissions({ medicalProfileId });
    const { hasInvoices } = useCollaboratorInvoices({
        medicalProfileId,
        collaboratorId,
        isOrganizationCollaborator: true,
    });
    const [selectedTab, setSelectedTab] = React.useState<Tabs>('details');

    const contactDetails = collaborator?.organization?.contactDetails;

    return (
        <TabView
            selectedTab={selectedTab}
            onChange={(tab) => setSelectedTab(tab as Tabs)}
            scrollable
            tabs={[
                {
                    title: intl('organizationDetails'),
                    value: 'details',
                    renderTabContent: () => (
                        <Stack vertical padding={getSpacing(8)} space={getSpacing(2)}>
                            <CollaboratorHeader
                                medicalProfileId={medicalProfileId}
                                collaboratorId={collaboratorId}
                                collaborator={collaborator}
                            />
                            <AssociatedCareTeamMember
                                medicalProfileId={medicalProfileId}
                                associatedCareTeamMember={collaborator?.associatedCareTeamMember}
                            />
                            <Stack vertical space={getSpacing(8)} style={{ paddingTop: getSpacing(10) }}>
                                <Text variant="h6">{collaborator?.organization?.metadata?.description}</Text>
                                {contactDetails ? <ContactDetailsDisplay contactDetails={contactDetails} /> : null}
                            </Stack>
                        </Stack>
                    ),
                },
                {
                    title: intl('permissions'),
                    value: 'permissions',
                    renderTabContent: () => (
                        <Stack vertical space={getSpacing(2)} style={{ paddingVertical: getSpacing(16) }}>
                            <FormStack>
                                <CenteredFormRow>
                                    <Text variant="h6">{intl('disclaimer')}</Text>
                                </CenteredFormRow>
                                <CenteredFormRow>
                                    <Field
                                        component={FormikInputs.PermissionsInput}
                                        name="appPermissions"
                                        isDisabled={isDisabled}
                                        mostPermissiveAppPermissions={permissions.permissions}
                                        initialAppPermissions={initialAppPermissions}
                                    />
                                </CenteredFormRow>
                            </FormStack>
                        </Stack>
                    ),
                },
                hasInvoices
                    ? {
                          title: intl('invoices'),
                          value: 'invoices',
                          renderTabContent: () => (
                              <CollaboratorInvoicesTab
                                  medicalProfileId={medicalProfileId}
                                  collaboratorId={collaboratorId}
                              />
                          ),
                      }
                    : null,
            ].filter(isPresent)}
        />
    );
};

export default React.memo(OrganizationCollaboratorView);
